'use client';

import { clsx } from 'clsx';
import { FC, useEffect, useState } from 'react';
import { BannerPromoStoryblok } from 'storyblok-types';
import { Icon, IconType } from 'atoms/Icon';
import { P } from 'atoms/Typography';
import { ReactMarkdown } from 'molecules/Markdown';
import { useMediaQuery } from 'modules/mui/hooks';
import { isValidDate, renderRichText } from 'modules/stroyblok/services';
import { useTimer } from './hooks/useTimer';
import { setBannerTime } from './services';
import styles from './styles.module.css';

export const PromoBanner: FC<{ blok: BannerPromoStoryblok }> = ({ blok }) => {
  const isValid = isValidDate(blok.start_date, blok.expiration_date);
  const { isMobile } = useMediaQuery();
  const [mobileCompactView, setMobileCompactView] = useState(isMobile);
  const { minutes, seconds, startTimer, stopTimer, timerState } = useTimer(
    parseInt(blok.timer),
    blok._uid,
  );
  const isCompactMode = isMobile && mobileCompactView;
  const toggleMobileView = () => setMobileCompactView(!mobileCompactView);
  const disableTimer = () => {
    setBannerTime(blok._uid, 0);
    stopTimer();
  };

  useEffect(() => {
    if (!isValid) {
      disableTimer();
    }

    return () => stopTimer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!timerState.isRunning && !!timerState.time && isValid) {
      startTimer();
    }

    if (!isValid) {
      disableTimer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerState]);

  return !timerState.isRunning || !isValid ? null : (
    <div className={clsx(styles.root, isCompactMode && styles.compactView)}>
      {isMobile && (
        <Icon
          type={mobileCompactView ? IconType.arrowUp : IconType.close}
          className={styles[mobileCompactView ? 'arrowButton' : 'closeButton']}
          onClick={toggleMobileView}
        />
      )}

      <div className={styles.firstColumn}>
        <p className={styles.discount}>
          {blok.discount_size}
          <span className={styles.discountMark}>%</span>
        </p>

        {isCompactMode && (
          <span className={styles.mobileDescription}>
            {blok.mobile_short_description}
          </span>
        )}

        {(!isCompactMode || !isMobile) && (
          <div className={styles.text}>
            <ReactMarkdown
              text={renderRichText(blok.description)}
              components={{
                p: ({ children }) => <P className={styles.text}>{children}</P>,
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.secondColumn}>
        <div className={styles.discountContainer}>
          {(!isCompactMode || !isMobile) && (
            <span className={styles.discountCode}>{blok.code}</span>
          )}
          <p className={styles.timer}>
            <span className={styles.minutes}>{minutes}</span>:
            <span className={styles.seconds}>{seconds}</span>
          </p>
        </div>

        {(!isCompactMode || !isMobile) && (
          <ReactMarkdown
            text={renderRichText(blok.additional_description)}
            className={styles.smallText}
          />
        )}
      </div>
    </div>
  );
};
